import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, BookOpen } from "lucide-react";

const API = "/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, success, failed, expired
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        const d = res.data;
        setData(d);
        if (d.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (d.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2500);
        } else {
          setStatus("failed");
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2500);
        else setStatus("failed");
      }
    };
    poll();
  }, [sessionId]);

  if (status === "checking") {
    return (
      <div translate="no" className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl text-center">
          <CardContent className="p-10 space-y-4">
            <Loader2 className="w-16 h-16 text-teal-500 mx-auto animate-spin" />
            <h2 className="text-xl font-bold text-slate-700">Verifying Payment...</h2>
            <p className="text-slate-400 text-sm">Please wait while we confirm your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "success" && data) {
    return (
      <div translate="no" className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl overflow-hidden" data-testid="payment-success-card">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-white mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white">Payment Successful!</h1>
          </div>
          <CardContent className="p-8 text-center space-y-5">
            <p className="text-slate-500">Thank you for renewing your subscription, <strong className="text-slate-800">{data.student_name}</strong>!</p>
            
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <span className="text-slate-500">Credits Added:</span>
                <span className="font-bold text-teal-700 text-lg">+{data.classes_purchased}</span>
                <span className="text-slate-500">Amount Paid:</span>
                <span className="font-bold text-slate-800">{data.amount} {data.currency_symbol}</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 py-3 bg-emerald-50 rounded-lg">
              <BookOpen className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold text-emerald-700">Your classes are ready to book!</span>
            </div>

            <Button onClick={() => navigate("/student-dashboard")}
              className="w-full h-12 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-semibold text-lg rounded-xl"
              data-testid="return-dashboard-btn">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Failed or expired
  return (
    <div translate="no" className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-xl text-center">
        <CardContent className="p-10 space-y-5">
          <XCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h2 className="text-xl font-bold text-slate-700">
            {status === "expired" ? "Payment Session Expired" : "Payment Could Not Be Verified"}
          </h2>
          <p className="text-slate-400 text-sm">Please try again or contact support.</p>
          <Button onClick={() => navigate("/student-dashboard")} variant="outline" className="mt-4">
            Return to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
